import React, { useState, useEffect } from "react";
import axios from "axios";
import Layout from "../../components/Layout";
import {
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type MasterData = {
  id: number;
  name: string;
};

const SystemAdminPage: React.FC = () => {
  const [selectedMaster, setSelectedMaster] = useState<string>(""); // 選択されたマスタのキー
  const [availableMasters, setAvailableMasters] = useState<
    { key: string; label: string }[]
  >([]); // 選択可能なマスタリスト
  const [data, setData] = useState<MasterData[]>([]); // 選択されたマスタデータ
  const [newItemName, setNewItemName] = useState("");
  const [editItem, setEditItem] = useState<MasterData | null>(null);
  const [loading, setLoading] = useState(false); // 全体のローディング状態
  const [deleting, setDeleting] = useState(false); // 削除中の状態
  const [sortConfig, setSortConfig] = useState<{
    key: keyof MasterData;
    direction: "asc" | "desc" | null;
  }>({
    key: "id",
    direction: null,
  });

  // 初回にサーバーから選択可能なマスタを取得
  useEffect(() => {
    const fetchAvailableMasters = async () => {
      try {
        const response = await axios.get<{ key: string; label: string }[]>(
          `${process.env.REACT_APP_BACKEND_URL}/api/main/system-admin/get-master-list`,
          { withCredentials: true }
        );
        setAvailableMasters(response.data);
      } catch (error) {
        console.error("マスタリストの取得に失敗しました:", error);
      }
    };

    fetchAvailableMasters();
  }, []);

  // マスタデータの取得
  const handleShowMasterData = async () => {
    if (selectedMaster) {
      try {
        const response = await axios.get<MasterData[]>(
          `${process.env.REACT_APP_BACKEND_URL}/api/main/system-admin/get-selected-masters/${selectedMaster}`,
          { withCredentials: true }
        );
        setData(response.data);
      } catch (error) {
        console.error("マスタデータの取得に失敗しました:", error);
      }
    }
  };

  const sortedData = [...data].sort((a, b) => {
    if (sortConfig.direction === null) return 0;
    if (a[sortConfig.key] < b[sortConfig.key])
      return sortConfig.direction === "asc" ? -1 : 1;
    if (a[sortConfig.key] > b[sortConfig.key])
      return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const requestSort = (key: keyof MasterData) => {
    let direction: "asc" | "desc" | null = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc")
      direction = "desc";
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key: keyof MasterData) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? faSortUp : faSortDown;
    }
    return faSort;
  };

  // 新しい項目をサーバーに追加
  const handleAddItem = async () => {
    if (newItemName.trim()) {
      try {
        const response = await axios.post<MasterData>(
          `${process.env.REACT_APP_BACKEND_URL}/api/main/system-admin/add-master/${selectedMaster}`,
          { name: newItemName },
          { withCredentials: true }
        );
        setData([...data, response.data]);
        setNewItemName("");
      } catch (error) {
        console.error("項目の追加に失敗しました:", error);
      }
    }
  };

  // 項目の編集をサーバーに保存
  const handleSaveItem = async () => {
    if (editItem) {
      try {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/main/system-admin/update-master/${selectedMaster}/${editItem.id}`,
          { name: editItem.name },
          { withCredentials: true } // 認証情報を含める
        );
        setData(data.map((d) => (d.id === editItem.id ? editItem : d)));
        setEditItem(null);
      } catch (error) {
        console.error("項目の保存に失敗しました:", error);
      }
    }
  };

  // 項目の削除
  const handleDeleteItem = async (id: number) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/main/system-admin/delete-master/${selectedMaster}/${id}`,
        { withCredentials: true } // 認証情報を含める
      );
      setData(data.filter((d) => d.id !== id));
    } catch (error) {
      console.error("項目の削除に失敗しました:", error);
    }
  };

  const columns: { key: keyof MasterData; label: string }[] = [
    { key: "id", label: "ID" },
    { key: "name", label: "項目名" },
  ];

  return (
    <Layout>
      <div className="p-10 bg-white rounded-3xl">
        {loading || deleting ? (
          // スケルトンスクリーン
          <>
            <div className="flex mb-4">
              <div className="w-10 h-10 bg-slate-200 rounded animate-pulse mr-4"></div>
              <div className="w-64 h-6 bg-slate-200 rounded animate-pulse"></div>
            </div>
            <div className="flex items-center justify-end mb-4">
              <div className="w-32 h-10 bg-slate-200 rounded animate-pulse"></div>
            </div>
            <div className="overflow-auto max-h-[70vh] border-2 border-slate-300 bg-slate-100 rounded-2xl">
              <div className="p-6 space-y-4">
                {Array.from({ length: 10 }).map((_, index) => (
                  <div key={index} className="flex space-x-4">
                    <div className="w-10 h-10 bg-slate-200 rounded animate-pulse"></div>
                    <div className="flex-1 h-8 bg-slate-200 rounded animate-pulse"></div>
                    <div className="flex-1 h-8 bg-slate-200 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          // 実データ表示
          <>
            <div className="flex mb-4">
              <div className="border-l-8 rounded-3xl border-slate-300 mr-4"></div>
              <h2 className="text-lg text-slate-500 font-black">
                マスタメンテナンス
              </h2>
            </div>
            <div className="flex justify-between">
              {/* マスタ選択と表示ボタン */}
              <div className="mb-4 flex items-center space-x-2">
                <select
                  value={selectedMaster}
                  onChange={(e) => setSelectedMaster(e.target.value)}
                  className="px-4 py-2 border rounded-lg focus:outline-none h-full"
                >
                  <option value="">マスタを選択</option>
                  {availableMasters.map((master) => (
                    <option key={master.key} value={master.key}>
                      {master.label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleShowMasterData}
                  className="bg-iso text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50 h-full"
                >
                  表示
                </button>
              </div>

              {/* 新しい項目の追加（マスタが選択された場合のみ表示） */}
              <div className="mb-4 flex items-center space-x-2 justify-end">
                <input
                  type="text"
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-full"
                  placeholder="項目名を入力"
                />
                <button
                  onClick={handleAddItem}
                  className="bg-iso text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50 h-full"
                >
                  追加
                </button>
              </div>
            </div>
            {/* テーブル表示 */}
            <div className="overflow-x-auto max-h-[70vh] border-2 border-slate-300 bg-slate-100 rounded-2xl">
              <table className="min-w-full text-slate-600 border-collapse table-auto">
                <thead className="sticky top-0 bg-slate-100 border-b-2 border-slate-300-100">
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        onClick={() => requestSort(column.key)}
                        className="py-4 px-2 cursor-pointer text-center"
                      >
                        <div className="flex items-center justify-center">
                          {column.label}
                          <FontAwesomeIcon
                            icon={getSortIcon(column.key)}
                            className="ml-1"
                          />
                        </div>
                      </th>
                    ))}
                    <th className="py-2 px-4 border-b text-center">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((item, index) => (
                    <tr
                      key={item.id}
                      className={`${
                        index % 2 === 0 ? "bg-white" : "bg-slate-50"
                      } hover:bg-[#E4F4F7] cursor-pointer text-center`}
                    >
                      <td className="py-2 px-4 text-center">{item.id}</td>
                      <td className="py-2 px-4 text-center">
                        {editItem && editItem.id === item.id ? (
                          <input
                            type="text"
                            value={editItem.name}
                            onChange={(e) =>
                              setEditItem({
                                ...editItem,
                                name: e.target.value,
                              })
                            }
                            className="w-full px-2 py-1 border rounded"
                          />
                        ) : (
                          item.name
                        )}
                      </td>
                      <td className="py-2 px-4 text-center">
                        <div className="flex justify-center space-x-10">
                          {editItem && editItem.id === item.id ? (
                            <button
                              onClick={handleSaveItem}
                              className="bg-iso text-white py-1 px-4 rounded-3xl hover:bg-opacity-50"
                            >
                              保存
                            </button>
                          ) : (
                            <button
                              onClick={() => setEditItem(item)}
                              className="bg-slate-600 text-white py-1 px-4 rounded-3xl hover:bg-opacity-50"
                            >
                              編集
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteItem(item.id)}
                            className="bg-slate-600 text-white py-1 px-4 rounded-3xl hover:bg-opacity-50"
                          >
                            削除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default SystemAdminPage;
